import { getCoverageLineConfig } from '@common/config';
import { DocumentFormat } from '@common/types/submission-placement-task-config';
import { getProposalQuoteComparisonConfig } from '@common/utils';
import { compact, isEmpty, uniq } from 'lodash';
import { useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { Box, Button, Stack } from '@common-components';
import {
  BrandingMode,
  CompareQuotesUrlParam,
  CoverageLine,
  PlacementType,
  PromptResultFeature,
  SubmissionDraftType,
} from 'enums';
import { useBoolean, useMutatePromptResult, useToast } from 'hooks';
import { useMutateSubmissionDraft } from 'hooks/api/submission-draft';
import { messages } from 'i18n';
import { logger } from 'utils';
import SplitButton, { SplitButtonOption } from 'components/SplitButton';
import { QuoteComparisonInsightsRoutes } from 'broker/broker-routes';
import { RouteKey } from 'broker/enums';
import { useNavigateBack, useNavigateToStep } from 'broker/hooks';

import { useConvertApi } from 'broker/hooks/useConvertApi';
import { insightsFormFieldsConfig } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/form-methods/config/insightsFieldsConfig';
import { InsightsFieldsNames } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/form-methods/enums';
import { PdfGenerationStatus } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/types';
import { useBrandConfig } from 'broker/pages/SubmissionWorkspacePage/contexts/BrandConfigProvider';
import { useWorkspaceUrls } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import FooterStepper from './FooterStepper';
import { QuoteComparisonProps, quoteComparisonSteps } from './types';

const websiteBaseUrl = process.env.REACT_APP_WEBSITE_BASE_URL;

export default function Footer(quoteComparisonProps: QuoteComparisonProps) {
  const {
    currentStep,
    pdfGenerationStatus,
    quotes,
    expiringPolicyFileId,
    savedDraft,
    quoteComparisonFileId,
    setIsDirty,
    isDirty,
    insightPopulatedByAI,
  } = quoteComparisonProps;
  const { compareQuotesSideBySideUrl } = useWorkspaceUrls();
  const { createSubmissionDraft, updateSubmissionDraft } = useMutateSubmissionDraft();
  const { showToast } = useToast();
  const { getValues } = useFormContext();
  const { partialSubmission, reFetchWorkspace } = useSubmissionsWorkspace();
  const { createPromptResult } = useMutatePromptResult();

  const navigateBack = useNavigateBack();
  const navigateToStep = useNavigateToStep();

  const { isClientBranded, isLoading: loadingBrandConfig } = useBrandConfig();

  const quoteComparisonConfig = getProposalQuoteComparisonConfig(partialSubmission!);

  const { clonePdfToDocx } = useConvertApi();
  const [cloningPdfToDocx, { on: setCloningPdfToDocx, off: setNotCloningPdfToDocx }] = useBoolean(false);

  const isLoading = cloningPdfToDocx || loadingBrandConfig;
  const isFinalStep = currentStep === QuoteComparisonInsightsRoutes.Upload;

  const handleChangeStep = (
    step: QuoteComparisonInsightsRoutes,
    direction: 'next' | 'prev',
    selectedBrandingMode?: BrandingMode,
  ) => {
    navigateToStep(quoteComparisonSteps[quoteComparisonSteps.indexOf(step) + (direction === 'next' ? 1 : -1)], {
      routeKey: RouteKey.QuoteComparisonInsights,
      state: {
        brandingMode: selectedBrandingMode,
      },
    });
  };

  const openSideBySideReview = () => {
    const urlParams = new URLSearchParams({
      [CompareQuotesUrlParam.QuoteIds]: quotes.map((quote) => quote.id).join(','),
    });
    if (expiringPolicyFileId) {
      urlParams.append(CompareQuotesUrlParam.ExpiringPolicyFileId, expiringPolicyFileId);
    }

    window.open(`${websiteBaseUrl}${compareQuotesSideBySideUrl}?${urlParams.toString()}`, '_blank');
  };

  const disabledSubmitButton = useMemo(
    () =>
      currentStep === QuoteComparisonInsightsRoutes.Upload &&
      [PdfGenerationStatus.Loading, PdfGenerationStatus.Error].includes(pdfGenerationStatus),
    [currentStep, pdfGenerationStatus],
  );

  const saveModifiedResult = () => {
    const modifiedResult = getValues(insightsFormFieldsConfig[InsightsFieldsNames.Insights].name);
    const aiInputData = {
      quoteIds: quotes.map((quote) => quote.id),
      expiringPolicyFileId,
      submissionId: partialSubmission!.id,
    };
    const originalResult =
      insightPopulatedByAI?.result || savedDraft?.metadata?.quoteComparison?.originalAIResponse || '';
    createPromptResult.mutateAsync({
      data: {
        result: originalResult,
        input: aiInputData,
        modifiedResult,
        feature: PromptResultFeature.QuoteCompareV2,
        submissionId: partialSubmission!.id,
      },
    });
  };

  const onFinalStep = () => {
    navigateBack();
  };

  const buildDraftTitle = () => {
    const coverageLines = compact(
      uniq(quotes.flatMap((quote) => quote.coverageLines)).map(
        (coverageLine) => getCoverageLineConfig?.(coverageLine as CoverageLine)?.text,
      ),
    ).join(', ');
    const marketNames = quotes.map((quote) => quote.marketName).join(', ');
    const expiringPolicy = expiringPolicyFileId ? ` + ${messages.quoteComparisonPage.compose.expiringPolicy}` : '';
    return `${coverageLines}${isEmpty(coverageLines) ? '' : ': '}${marketNames}${expiringPolicy}`;
  };

  const handleSaveDraft = async () => {
    try {
      const content = getValues(insightsFormFieldsConfig[InsightsFieldsNames.Insights].name);
      const submissionId = partialSubmission!.id;

      if (savedDraft) {
        await updateSubmissionDraft({ id: savedDraft.id, data: { content } });
        showToast('success', { message: 'Draft updated successfully' });
      } else {
        const draftData = {
          submissionId,
          draftType: SubmissionDraftType.QuoteComparison,
          metadata: {
            quoteComparison: {
              title: buildDraftTitle(),
              quoteIds: quotes.map((quote) => quote.id),
              expiringPolicyId: expiringPolicyFileId,
              originalAIResponse: insightPopulatedByAI?.result,
            },
          },
          content,
        };
        await createSubmissionDraft({ data: draftData });
        showToast('success', { message: 'Draft saved successfully' });
      }
      await reFetchWorkspace();
      // Force React to flush the dirty flag update before we navigate away
      flushSync(() => {
        setIsDirty(false);
      });
      navigateBack();
    } catch {
      showToast('error', { message: 'Failed to save draft' });
    }
  };

  const onProceedClicked = async (isDoc: boolean, selectedBrandingMode?: BrandingMode) => {
    if (currentStep === QuoteComparisonInsightsRoutes.Upload) {
      if (isDoc) {
        try {
          setCloningPdfToDocx();
          await clonePdfToDocx(quoteComparisonFileId);
        } catch (e: any) {
          logger.log('error', {
            message: 'Failed to convert PDF to DOCX in Quote Comparison',
            error: e,
            fileIdToAttach: quoteComparisonFileId,
          });
          throw new Error('We failed to generate docx, please try again');
        } finally {
          setNotCloningPdfToDocx();
        }
      }
      saveModifiedResult();
      onFinalStep();
    } else {
      // If the user was editing a saved draft, we save it first
      if (savedDraft && isDirty) {
        await handleSaveDraft();
      }
      handleChangeStep(currentStep, 'next', selectedBrandingMode);
    }
  };

  const getComposeStepButtons = (): SplitButtonOption[] => {
    const flowBrandedButton: SplitButtonOption = {
      label: messages.quoteComparisonPage.buttons.nextGeneratePDF,
      onClick: async () => {
        await onProceedClicked(false, BrandingMode.Flow);
      },
      disabled: disabledSubmitButton,
    };

    const clientBrandedButton: SplitButtonOption = {
      label: messages.quoteComparisonPage.buttons.nextGenerateBrandedPDF,
      onClick: async () => {
        await onProceedClicked(false, BrandingMode.Organization);
      },
      disabled: disabledSubmitButton,
    };

    // Only show client branded button if client branding is available
    if (!isClientBranded) {
      return [flowBrandedButton];
    }

    // Determine button order based on config
    // if no config, fallback to legacy behavior: direct placement -> client branded, else flow branded
    const preferClientBranded =
      quoteComparisonConfig?.clientBranded ?? partialSubmission?.placementType === PlacementType.Direct;

    return preferClientBranded ? [clientBrandedButton, flowBrandedButton] : [flowBrandedButton, clientBrandedButton];
  };

  const getUploadStepButtons = (): SplitButtonOption[] => {
    const savePdfButton: SplitButtonOption = {
      label: messages.quoteComparisonPage.buttons.savePDF,
      onClick: async () => {
        await onProceedClicked(false);
      },
      disabled: disabledSubmitButton,
    };

    const convertToDocxButton: SplitButtonOption = {
      label: messages.quoteComparisonPage.buttons.saveDoc,
      onClick: async () => {
        await onProceedClicked(true);
      },
      disabled: disabledSubmitButton,
    };

    // Determine button order based on config
    // if no config, fallback to legacy behavior: direct placement -> docx, else pdf
    const preferDocx =
      quoteComparisonConfig?.format === DocumentFormat.DOCX ||
      partialSubmission?.placementType === PlacementType.Direct;

    return preferDocx ? [convertToDocxButton, savePdfButton] : [savePdfButton, convertToDocxButton];
  };

  const getButtons = (): SplitButtonOption[] => {
    switch (currentStep) {
      case QuoteComparisonInsightsRoutes.Compose:
        return getComposeStepButtons();
      case QuoteComparisonInsightsRoutes.Upload:
        return getUploadStepButtons();
      default:
        return [];
    }
  };

  return (
    <Stack direction="row" gap={1} justifyContent="center">
      <Box flex={1} />
      <Stack flex={1} alignItems="center">
        <FooterStepper handleChangeStep={handleChangeStep} currentStep={currentStep} />
      </Stack>
      <Stack flex={1} direction="row" gap={1} justifyContent="flex-end" alignItems="center">
        <Stack direction="row" gap={1}>
          {!isFinalStep && (
            <Button variant="text" onClick={openSideBySideReview}>
              {messages.quoteComparisonPage.buttons.sideBySide}
            </Button>
          )}
          {currentStep === QuoteComparisonInsightsRoutes.Compose && (
            <Button variant="outlined" onClick={handleSaveDraft}>
              {messages.buttons.saveForLater}
            </Button>
          )}
          <SplitButton
            loading={isLoading}
            key={currentStep}
            config={{
              options: getButtons(),
            }}
            variant="contained"
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
